'use client';

import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';

/**
 * ПЛАШКА-СКЕЛЕТ НАБОРА (`.k-skel` в kit.css): держит место элемента, который ещё едет
 * (форма Stripe, ряд кнопок кошельков), ровно его высотой — замена «одним кадром», без
 * сдвига (ТЗ ~/projects/PAY-SCREEN-2-TZ.md §1.4). Цвет — роль `--line`, скругление —
 * ступень паспорта; своих чисел нет: высоту даёт вызывающий из того же места, откуда её
 * берёт настоящий элемент (`--k-input-h`, `--h-row-m`).
 */
export function KSkeleton({
  height,
  width,
  radius = 'r',
  className,
}: {
  /** Высота плашки, px — равна высоте элемента, который встанет на её место. */
  height: number;
  /** Ширина; по умолчанию — на всю ширину родителя. */
  width?: number | string;
  /** Ступень скругления паспорта: `xs` 8 · `sm` 12 · `r` 14 · `round` (капсула). */
  radius?: 'xs' | 'sm' | 'r' | 'round';
  className?: string;
}) {
  const style: CSSProperties = { height, width: width ?? '100%' };
  return <span className={cx('k-skel', `k-skel--${radius}`, className)} style={style} aria-hidden="true" />;
}

/**
 * ПЛАШКА С ПОДПИСЬЮ — поле формы, которого ещё нет: подпись настоящим текстом над плашкой
 * высоты поля (так выглядит поле Stripe «Номер карты» до прихода iframe). Подпись
 * рисуется словом, а не полосой: слово известно заранее, и после подмены на месте
 * плашки встаёт поле с той же подписью — ничего не прыгает.
 */
export function KSkeletonField({
  label,
  height,
  radius = 'r',
  className,
}: {
  label: ReactNode;
  /** Высота поля, px (`--k-input-h`). */
  height: number;
  radius?: 'xs' | 'sm' | 'r' | 'round';
  className?: string;
}) {
  return (
    <span className={cx('k-skel-field', className)}>
      <span className="k-skel-field__label">{label}</span>
      <KSkeleton height={height} radius={radius} />
    </span>
  );
}
